// hooks/useSmoothScroll.ts
"use client";

import React from 'react';
import { usePathname, useRouter } from 'next/navigation';
import { useEffect } from 'react';
import { useLenis } from 'lenis/react';


interface ScrollOptions {
  offset?: number;
  duration?: number;
  easing?: (t: number) => number;
  targetPage?: string; // Page where section exists (default: '/')
}

/**
 * Custom hook for smooth scrolling with Lenis
 */
export const useSmoothScroll = () => {
  const pathname = usePathname();
  const router = useRouter();
  const lenis = useLenis();

  // Default options for scrolling
  const defaultOptions: ScrollOptions = React.useMemo(() => ({
    offset: -120, // Increased to account for navbar height (approx 104px + padding)
    duration: 1.2,
    easing: (t: number) => Math.min(1, 1.001 - Math.pow(2, -10 * t)), // Smooth easing
    targetPage: '/', // Default target page is home
  }), []);

  /**
   * Check if section exists on current page
   */
  const sectionExists = (sectionId: string): boolean => {
    return !!document.getElementById(sectionId);
  };

  /**
   * Scroll to an element by ID
   * @param sectionId - The ID of the element to scroll to
   * @param options - Custom scroll options (optional)
   */
  const scrollToSection = (sectionId: string, customOptions?: Partial<ScrollOptions>) => {
    const options = { ...defaultOptions, ...customOptions };
    const isOnTargetPage = pathname === options.targetPage;
    
    // If already on target page, check if section exists
    if (isOnTargetPage) {
      // Check if section exists
      if (sectionExists(sectionId)) {
        performScroll(sectionId, options);
      } else {
        console.warn(`Section #${sectionId} not found on current page.`);
      }
    } else {
      // If not on target page, navigate to target page and then scroll
      // Store the target section in sessionStorage
      sessionStorage.setItem('scrollToSection', sectionId);
      sessionStorage.setItem('scrollOptions', JSON.stringify(options));
      router.push(options.targetPage || '/');
    }
  };

  /**
   * Actually perform the scroll operation
   */
  const performScroll = React.useCallback((sectionId: string, options: ScrollOptions) => {
    const section = document.getElementById(sectionId);
    if (!section) return;
    
    // Use lenis scroll if available
    if (lenis) {
      lenis.scrollTo(section, {
        offset: options.offset,
        duration: options.duration,
        easing: options.easing,
      });
    } else {
      // Fallback with manual offset calculation
      const elementPosition = section.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset + (options.offset || -120);
      
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  }, [lenis]);

  // Check for stored section to scroll to when component mounts or path changes
  useEffect(() => {
    const sectionToScroll = sessionStorage.getItem('scrollToSection');
    const storedOptions = sessionStorage.getItem('scrollOptions');
    
    if (sectionToScroll) {
      let options = defaultOptions;
      
      // Parse stored options if available
      if (storedOptions) {
        try {
          options = { ...options, ...JSON.parse(storedOptions) };
        } catch {
          console.error('Failed to parse stored scroll options');
        }
      }
      
      // Only proceed if we're on the target page
      if (pathname === (options.targetPage || '/')) {
        // Short delay to ensure the page has loaded
        const timer = setTimeout(() => {
          // Check if section exists after page load
          if (sectionExists(sectionToScroll)) {
            performScroll(sectionToScroll, options);
          } else {
            console.warn(`Section #${sectionToScroll} not found after navigation.`);
          }
          
          // Clean up sessionStorage
          sessionStorage.removeItem('scrollToSection');
          sessionStorage.removeItem('scrollOptions');
        }, 500);
        
        return () => clearTimeout(timer);
      }
    }
  }, [pathname, defaultOptions, performScroll]);

  return { 
    scrollToSection,
    sectionExists,
    isOnHomePage: pathname === '/'
  };
};